import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, Tag } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import blogPosts from '../data/blogData';

export default function Blog() {
  return (
    <>
      <Helmet>
        <title>Curtain & Blinds Blog Adelaide | Expert Tips & Guides | YN Dream Curtains</title>
        <meta name="description" content="Expert curtain, blind & shutter guides from Adelaide's leading window furnishing specialists. Tips on measuring, cleaning, choosing styles, and saving energy with curtains." />
        <meta name="keywords" content="curtain blog adelaide, curtain guides, curtain tips, blinds guide adelaide, window furnishing advice" />
        <link rel="canonical" href="https://yndreamcurtains.com/blog" />
        <meta name="geo.region" content="AU-SA" />
        <meta name="geo.placename" content="Adelaide" />
        <meta property="og:title" content="Curtain & Blinds Blog Adelaide | YN Dream Curtains" />
        <meta property="og:description" content="Expert curtain, blind & shutter guides from Adelaide's leading specialists." />
        <meta property="og:url" content="https://yndreamcurtains.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_AU" />
        <meta property="og:site_name" content="YN Dream Curtains" />
        <meta property="og:image" content="https://yndreamcurtains.com/logo512.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"Blog","name":"YN Dream Curtains Blog",
          "description":"Expert curtain, blind and shutter guides for Adelaide homeowners",
          "url":"https://yndreamcurtains.com/blog",
          "publisher":{"@type":"Organization","name":"YN Dream Curtains","logo":{"@type":"ImageObject","url":"https://yndreamcurtains.com/logo512.png"}},
          "blogPost": blogPosts.map(p => ({"@type":"BlogPosting","headline":p.title,"url":`https://yndreamcurtains.com/blog/${p.slug}`,"datePublished":p.date,"author":{"@type":"Organization","name":"YN Dream Curtains"}}))
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"BreadcrumbList",
          "itemListElement":[
            {"@type":"ListItem","position":1,"name":"Home","item":"https://yndreamcurtains.com/"},
            {"@type":"ListItem","position":2,"name":"Blog","item":"https://yndreamcurtains.com/blog"}
          ]
        })}</script>
      </Helmet>

      <style>{`
        .page-hero { background:var(--bg-warm); border-bottom:1px solid var(--border-soft); padding:120px 0 50px; }
        .blog-body { padding:60px 0 80px; background:var(--bg); }
        .blog-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
        .blog-card {
          background:var(--bg-card); border:1px solid var(--border-soft);
          overflow:hidden; transition:all 0.4s; text-decoration:none; color:inherit;
          display:flex; flex-direction:column; box-shadow:var(--shadow-sm);
          position:relative;
        }
        .blog-card::after {
          content:''; position:absolute; bottom:0; left:0; right:0;
          height:2px; background:var(--gold); transform:scaleX(0);
          transition:transform 0.4s; transform-origin:left;
        }
        .blog-card:hover::after { transform:scaleX(1); }
        .blog-card:hover { transform:translateY(-4px); box-shadow:var(--shadow-md); }
        .blog-card-img { width:100%; height:160px; object-fit:cover; display:block; transition:transform 0.6s; }
        .blog-card:hover .blog-card-img { transform:scale(1.05); }
        .blog-card-img-wrap { overflow:hidden; position:relative; }
        .blog-card-category {
          position:absolute; top:10px; left:10px;
          background:var(--gold); color:#fff;
          font-family:'Outfit',sans-serif; font-size:0.48rem; font-weight:700;
          letter-spacing:0.16em; text-transform:uppercase; padding:4px 10px;
        }
        .blog-card-body { padding:16px 16px 18px; flex:1; display:flex; flex-direction:column; }
        .blog-card-meta {
          display:flex; align-items:center; gap:10px; margin-bottom:8px;
          font-family:'DM Sans',sans-serif; font-size:0.72rem; color:var(--text-light);
        }
        .blog-card-meta svg { width:11px; height:11px; }
        .blog-card-title {
          font-family:'Cormorant Garamond',serif; font-size:1.1rem; font-weight:600;
          color:var(--text); line-height:1.25; margin-bottom:8px;
          transition:color 0.3s;
        }
        .blog-card:hover .blog-card-title { color:var(--gold); }
        .blog-card-excerpt {
          font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:300;
          color:var(--text-dim); line-height:1.55; margin-bottom:14px; flex:1;
        }
        .blog-card-link {
          font-family:'Outfit',sans-serif; font-size:0.54rem; font-weight:700;
          letter-spacing:0.16em; text-transform:uppercase; color:var(--gold);
          display:inline-flex; align-items:center; gap:5px; transition:gap 0.3s;
        }
        .blog-card:hover .blog-card-link { gap:9px; }
        @media(max-width:1100px) { .blog-grid { grid-template-columns:repeat(3,1fr); } }
        @media(max-width:768px) { .blog-grid { grid-template-columns:repeat(2,1fr); } }
        @media(max-width:500px) { .blog-grid { grid-template-columns:1fr; } }
      `}</style>

      {/* Hero */}
      <div className="page-hero">
        <div className="container">
          <AnimatedSection>
            <div className="section-label">Our Blog</div>
            <h1 className="section-title" style={{marginBottom:12}}>
              Expert Guides & Tips <em>Adelaide</em>
            </h1>
            <p className="section-desc">
              Professional curtain, blind and shutter advice from Adelaide's trusted window furnishing specialists. Guides, tips, and trends to help you make the perfect choice.
            </p>
          </AnimatedSection>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="blog-body">
        <div className="container">
          <AnimatedSection>
            <div className="blog-grid">
              {blogPosts.map(post => (
                <Link to={`/blog/${post.slug}`} className="blog-card" key={post.slug}>
                  <div className="blog-card-img-wrap">
                    <img src={post.image} alt={post.title} className="blog-card-img" loading="lazy" width="400" height="160" />
                    <span className="blog-card-category">{post.category}</span>
                  </div>
                  <div className="blog-card-body">
                    <div className="blog-card-meta">
                      <span style={{display:'flex',alignItems:'center',gap:4}}><Clock /> {post.date}</span>
                      <span style={{display:'flex',alignItems:'center',gap:4}}><Tag /> {post.readTime}</span>
                    </div>
                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <span className="blog-card-link">Read More <ArrowRight size={12} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </>
  );
}
